import find from 'lodash/find';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import upperCase from 'lodash/upperCase';
import sortBy from 'lodash/sortBy';

import type { Driver } from '../types/driver';

export const OCCUPATION_EXCLUSION_STATES = ['CA', 'GA', 'NY', 'MA'];
export const GEICO_OCCUPATION_EXCLUSION_STATES = ['MA'];

export interface OCCUPATION {
  value: string;
  label: string;
  industry: string | null;
  occupation: string | null;
}

export const OTHER_OCCUPATION = 'Other';

/**
 * List of employment status displayed on 'occupation' and 'occupationspouse' step.
 */
export const EMPLOYMENT_OPTIONS: OCCUPATION[] = [
  {
    value: '1',
    label: 'Employed',
    industry: 'Business/Sales/Office',
    occupation: 'Other',
  },
  {
    value: '2',
    label: 'Full-time student',
    industry: 'Student',
    occupation: 'Graduate Student',
  },
  {
    value: '3',
    label: 'Homemaker',
    industry: 'Homemaker/House person',
    occupation: 'Homemaker/House person',
  },
  {
    value: '4',
    label: 'Retired',
    industry: 'Retired',
    occupation: 'Retired',
  },
  {
    value: '5',
    label: 'Disabled',
    industry: 'Disabled',
    occupation: 'Disabled',
  },
  {
    value: '6',
    label: 'Unemployed',
    industry: 'Unemployed',
    occupation: 'Unemployed',
  },
];

export const NON_EMPLOYMENT_OPTIONS: OCCUPATION[] = EMPLOYMENT_OPTIONS.filter(
  (item) => item.industry !== 'Business/Sales/Office' || item.occupation !== 'Other'
);

export const GEICO_CA_DECLINE_ANSWER_OCCUPATION: OCCUPATION = {
  value: '100000',
  label: 'I decline to provide',
  industry: null,
  occupation: null,
};

export const EMPLOYMENT_OPTIONS_WITH_DECLINE: OCCUPATION[] = [
  ...EMPLOYMENT_OPTIONS,
  GEICO_CA_DECLINE_ANSWER_OCCUPATION,
];

const OCCUPATIONS: OCCUPATION[] = [
  {
    value: '1',
    label: 'Agriculture Inspector/Grader',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Agriculture Inspector/Grader',
  },
  {
    value: '2',
    label: 'Arborist',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Arborist',
  },
  {
    value: '3',
    label: 'Farm/Ranch Owner',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Farm/Ranch Owner',
  },
  {
    value: '4',
    label: 'Farm/Ranch Worker',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Farm/Ranch Worker',
  },
  {
    value: '5',
    label: 'Fisherman',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Fisherman',
  },
  {
    value: '6',
    label: 'Florist',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Florist',
  },
  {
    value: '7',
    label: 'Landscaper',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Landscaper',
  },
  {
    value: '8',
    label: 'Logger',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Logger',
  },
  {
    value: '9',
    label: 'Mill Worker',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Mill Worker',
  },
  {
    value: '10',
    label: 'Nursery Worker',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Nursery Worker',
  },
  {
    value: '11',
    label: 'Ranger',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Ranger',
  },
  {
    value: '12',
    label: 'Supervisor',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Supervisor',
  },
  {
    value: '13',
    label: 'Timber Grader/Scaler',
    industry: 'Agriculture/Forestry/Fishing',
    occupation: 'Timber Grader/Scaler',
  },
  {
    value: '14',
    label: 'Actor',
    industry: 'Art/Design/Media',
    occupation: 'Actor',
  },
  {
    value: '15',
    label: 'Announcer/Broadcaster',
    industry: 'Art/Design/Media',
    occupation: 'Announcer/Broadcaster',
  },
  {
    value: '16',
    label: 'Artist/Animator',
    industry: 'Art/Design/Media',
    occupation: 'Artist/Animator',
  },
  {
    value: '17',
    label: 'Author/Writer',
    industry: 'Art/Design/Media',
    occupation: 'Author/Writer',
  },
  {
    value: '18',
    label: 'Choreographer/Dancer',
    industry: 'Art/Design/Media',
    occupation: 'Choreographer/Dancer',
  },
  {
    value: '19',
    label: 'Composer/Director',
    industry: 'Art/Design/Media',
    occupation: 'Composer/Director',
  },
  {
    value: '20',
    label: 'Curator',
    industry: 'Art/Design/Media',
    occupation: 'Curator',
  },
  {
    value: '21',
    label: 'Designer',
    industry: 'Art/Design/Media',
    occupation: 'Designer',
  },
  {
    value: '22',
    label: 'Editor',
    industry: 'Art/Design/Media',
    occupation: 'Editor',
  },
  {
    value: '23',
    label: 'Journalist/Reporter',
    industry: 'Art/Design/Media',
    occupation: 'Journalist/Reporter',
  },
  {
    value: '24',
    label: 'Musician/Singer',
    industry: 'Art/Design/Media',
    occupation: 'Musician/Singer',
  },
  {
    value: '25',
    label: 'Photographer',
    industry: 'Art/Design/Media',
    occupation: 'Photographer',
  },
  {
    value: '26',
    label: 'Printer',
    industry: 'Art/Design/Media',
    occupation: 'Printer',
  },
  {
    value: '27',
    label: 'Producer',
    industry: 'Art/Design/Media',
    occupation: 'Producer',
  },
  {
    value: '28',
    label: 'Production Crew',
    industry: 'Art/Design/Media',
    occupation: 'Production Crew',
  },
  {
    value: '29',
    label: 'Projectionist',
    industry: 'Art/Design/Media',
    occupation: 'Projectionist',
  },
  {
    value: '30',
    label: 'Ticket Sales/Usher',
    industry: 'Art/Design/Media',
    occupation: 'Ticket Sales/Usher',
  },
  {
    value: '31',
    label: 'Accountant/Auditor',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Accountant/Auditor',
  },
  {
    value: '32',
    label: 'Analyst',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Analyst',
  },
  {
    value: '33',
    label: 'Appraiser',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Appraiser',
  },
  {
    value: '34',
    label: 'Bookkeeper',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Bookkeeper',
  },
  {
    value: '35',
    label: 'Branch Manager',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Branch Manager',
  },
  {
    value: '36',
    label: 'Clerk',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Clerk',
  },
  {
    value: '37',
    label: 'Collections',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Collections',
  },
  {
    value: '38',
    label: 'Controller',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Controller',
  },
  {
    value: '39',
    label: 'Credit/Loan Manager',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Manager-Credit/Loan',
  },
  {
    value: '40',
    label: 'Financial Advisor',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Financial Advisor',
  },
  {
    value: '41',
    label: 'Investment Banker',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Investment Banker',
  },
  {
    value: '42',
    label: 'Investor',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Investor',
  },
  {
    value: '43',
    label: 'Loan/Escrow Processor',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Loan/Escrow Processor',
  },
  {
    value: '44',
    label: 'Portfolio/Production Manager',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Manager-Portfolio/Production',
  },
  {
    value: '45',
    label: 'Property Manager',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Manager-Property',
  },
  {
    value: '46',
    label: 'Realtor',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Realtor',
  },
  {
    value: '47',
    label: 'Receptionist/Secretary',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Receptionist/Secretary',
  },
  {
    value: '48',
    label: 'Teller',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Teller',
  },
  {
    value: '49',
    label: 'Trader, Financial Instruments',
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Trader, Financial Instruments',
  },
  {
    value: '50',
    label: 'Account Executive',
    industry: 'Business/Sales/Office',
    occupation: 'Account Executive',
  },
  {
    value: '51',
    label: 'Administrative Assistant',
    industry: 'Business/Sales/Office',
    occupation: 'Administrative Assistant',
  },
  {
    value: '52',
    label: 'Buyer',
    industry: 'Business/Sales/Office',
    occupation: 'Buyer',
  },
  {
    value: '53',
    label: 'Cashier',
    industry: 'Business/Sales/Office',
    occupation: 'Cashier/Checker',
  },
  {
    value: '54',
    label: 'Consultant',
    industry: 'Business/Sales/Office',
    occupation: 'Consultant',
  },
  {
    value: '55',
    label: 'Department Store Manager',
    industry: 'Business/Sales/Office',
    occupation: 'Manager - Department/Store',
  },
  {
    value: '56',
    label: 'District Manager',
    industry: 'Business/Sales/Office',
    occupation: 'Manager - District',
  },
  {
    value: '57',
    label: 'Finance Manager',
    industry: 'Business/Sales/Office',
    occupation: 'Manager - Finance',
  },
  {
    value: '58',
    label: 'General Operations Manager',
    industry: 'Business/Sales/Office',
    occupation: 'Manager - General Operations',
  },
  {
    value: '59',
    label: 'HR Representative',
    industry: 'Business/Sales/Office',
    occupation: 'HR Representative',
  },
  {
    value: '60',
    label: 'HR/PR Manager',
    industry: 'Business/Sales/Office',
    occupation: 'HR/PR Manager',
  },
  {
    value: '61',
    label: 'Marketing',
    industry: 'Business/Sales/Office',
    occupation: 'Marketing',
  },
  {
    value: '62',
    label: 'Messenger/Courier',
    industry: 'Business/Sales/Office',
    occupation: 'Messenger/Courier',
  },
  {
    value: '63',
    label: 'Office Manager',
    industry: 'Business/Sales/Office',
    occupation: 'Manager/Supervisor - Office',
  },
  {
    value: '64',
    label: 'Other',
    industry: 'Business/Sales/Office',
    occupation: 'Other',
  },
  {
    value: '65',
    label: 'Sales',
    industry: 'Business/Sales/Office',
    occupation: 'Sales - Retail/Wholesale',
  },
  {
    value: '66',
    label: 'Boiler Operator/Maker',
    industry: 'Construction/Energy Trades',
    occupation: 'Boiler Operator/Maker',
  },
  {
    value: '67',
    label: 'Bricklayer/Mason',
    industry: 'Construction/Energy Trades',
    occupation: 'Bricklayer/Mason',
  },
  {
    value: '68',
    label: 'Carpenter',
    industry: 'Construction/Energy Trades',
    occupation: 'Carpenter',
  },
  {
    value: '69',
    label: 'Carpet Installer',
    industry: 'Construction/Energy Trades',
    occupation: 'Carpet Installer',
  },
  {
    value: '70',
    label: 'Concrete Worker',
    industry: 'Construction/Energy Trades',
    occupation: 'Concrete Worker',
  },
  {
    value: '71',
    label: 'Construction Worker',
    industry: 'Construction/Energy Trades',
    occupation: 'Construction Worker',
  },
  {
    value: '72',
    label: 'Contractor',
    industry: 'Construction/Energy Trades',
    occupation: 'Contractor',
  },
  {
    value: '73',
    label: 'Crane Operator',
    industry: 'Construction/Energy Trades',
    occupation: 'Crane Operator',
  },
  {
    value: '74',
    label: 'Electrician/Linesman',
    industry: 'Construction/Energy Trades',
    occupation: 'Electrician/Linesman',
  },
  {
    value: '75',
    label: 'Elevator Technician/Installer',
    industry: 'Construction/Energy Trades',
    occupation: 'Elevator Technician/Installer',
  },
  {
    value: '76',
    label: 'Equipment Operator',
    industry: 'Construction/Energy Trades',
    occupation: 'Equipment Operator',
  },
  {
    value: '77',
    label: 'Floor Layer/Finisher',
    industry: 'Construction/Energy Trades',
    occupation: 'Floor Layer/Finisher',
  },
  {
    value: '78',
    label: 'Foreman/Supervisor',
    industry: 'Construction/Energy Trades',
    occupation: 'Foreman/Supervisor',
  },
  {
    value: '79',
    label: 'Heat/Air Technician',
    industry: 'Construction/Energy Trades',
    occupation: 'Heat/Air Technician',
  },
  {
    value: '80',
    label: 'Laborer/Worker',
    industry: 'Construction/Energy Trades',
    occupation: 'Laborer/Worker',
  },
  {
    value: '81',
    label: 'Metalworker',
    industry: 'Construction/Energy Trades',
    occupation: 'Metalworker',
  },
  {
    value: '82',
    label: 'Miner',
    industry: 'Construction/Energy Trades',
    occupation: 'Miner',
  },
  {
    value: '83',
    label: 'Oil/Gas Driller/Rig Operator',
    industry: 'Construction/Energy Trades',
    occupation: 'Oil/Gas Driller/Rig Operator',
  },
  {
    value: '84',
    label: 'Painter',
    industry: 'Construction/Energy Trades',
    occupation: 'Painter',
  },
  {
    value: '85',
    label: 'Plaster/Drywall/Stucco',
    industry: 'Construction/Energy Trades',
    occupation: 'Plaster/Drywall/Stucco',
  },
  {
    value: '86',
    label: 'Plumber',
    industry: 'Construction/Energy Trades',
    occupation: 'Plumber',
  },
  {
    value: '87',
    label: 'Roofer',
    industry: 'Construction/Energy Trades',
    occupation: 'Roofer',
  },
  {
    value: '88',
    label: 'Utility Worker',
    industry: 'Construction/Energy Trades',
    occupation: 'Utility Worker',
  },
  {
    value: '89',
    label: 'Welder',
    industry: 'Construction/Energy Trades',
    occupation: 'Welder',
  },
  {
    value: '90',
    label: 'Disabled',
    industry: 'Disabled',
    occupation: 'Disabled',
  },
  {
    value: '91',
    label: 'Audio-Visual Tech',
    industry: 'Education/Library',
    occupation: 'Audio-Visual Tech',
  },
  {
    value: '92',
    label: 'Child/Daycare Worker',
    industry: 'Education/Library',
    occupation: 'Child/Daycare Worker',
  },
  {
    value: '93',
    label: 'Counselor',
    industry: 'Education/Library',
    occupation: 'Counselor',
  },
  {
    value: '94',
    label: 'Graduate Teaching Assistant',
    industry: 'Education/Library',
    occupation: 'Graduate Teaching Assistant',
  },
  {
    value: '95',
    label: 'Vocation Instructor',
    industry: 'Education/Library',
    occupation: 'Instructor-Vocation',
  },
  {
    value: '96',
    label: 'Librarian/Curator',
    industry: 'Education/Library',
    occupation: 'Librarian/Curator',
  },
  {
    value: '97',
    label: 'Principal',
    industry: 'Education/Library',
    occupation: 'Principal',
  },
  {
    value: '98',
    label: 'Professor',
    industry: 'Education/Library',
    occupation: 'Professor',
  },
  {
    value: '99',
    label: 'Superintendent',
    industry: 'Education/Library',
    occupation: 'Superintendent',
  },
  {
    value: '100',
    label: 'Teacher, College',
    industry: 'Education/Library',
    occupation: 'Teacher, College',
  },
  {
    value: '101',
    label: 'Teacher, K-12',
    industry: 'Education/Library',
    occupation: 'Teacher, K-12',
  },
  {
    value: '102',
    label: 'Teaching Assistant/Aide',
    industry: 'Education/Library',
    occupation: 'Teaching Assistant/Aide',
  },
  {
    value: '103',
    label: 'Tutor',
    industry: 'Education/Library',
    occupation: 'Tutor',
  },
  {
    value: '104',
    label: 'Actuary',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Actuary',
  },
  {
    value: '105',
    label: 'Architect',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Architect',
  },
  {
    value: '106',
    label: 'Chemist',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Chemist',
  },
  {
    value: '107',
    label: 'Clinical Data Coordinator',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Clinical Data Coordinator',
  },
  {
    value: '108',
    label: 'Drafter',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Drafter',
  },
  {
    value: '109',
    label: 'Engineer',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Engineer',
  },
  {
    value: '110',
    label: 'Lab Assistant',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Lab Assistant',
  },
  {
    value: '111',
    label: 'Mathematician',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Mathematician',
  },
  {
    value: '112',
    label: 'Project Manager',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Manager-Project',
  },
  {
    value: '113',
    label: 'R&D Manager',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Manager-R&D',
  },
  {
    value: '114',
    label: 'Research Program Director',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Research Program Director',
  },
  {
    value: '115',
    label: 'Researcher',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Researcher',
  },
  {
    value: '116',
    label: 'Scientist',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Scientist',
  },
  {
    value: '117',
    label: 'Sociologist',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Sociologist',
  },
  {
    value: '118',
    label: 'Surveyor/Mapmaker',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Surveyor/Mapmaker',
  },
  {
    value: '119',
    label: 'Technician',
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Technician',
  },
  {
    value: '120',
    label: 'Baker',
    industry: 'Food Service/Hotel Services',
    occupation: 'Baker',
  },
  {
    value: '121',
    label: 'Bartender',
    industry: 'Food Service/Hotel Services',
    occupation: 'Bartender',
  },
  {
    value: '122',
    label: 'Bellhop',
    industry: 'Food Service/Hotel Services',
    occupation: 'Bellhop',
  },
  {
    value: '123',
    label: 'Busser',
    industry: 'Food Service/Hotel Services',
    occupation: 'Bus Person',
  },
  {
    value: '124',
    label: 'Caterer',
    industry: 'Food Service/Hotel Services',
    occupation: 'Caterer',
  },
  {
    value: '125',
    label: 'Chef',
    industry: 'Food Service/Hotel Services',
    occupation: 'Chef',
  },
  {
    value: '126',
    label: 'Concessionaire',
    industry: 'Food Service/Hotel Services',
    occupation: 'Concessionaire',
  },
  {
    value: '127',
    label: 'Concierge',
    industry: 'Food Service/Hotel Services',
    occupation: 'Concierge',
  },
  {
    value: '128',
    label: 'Cook',
    industry: 'Food Service/Hotel Services',
    occupation: 'Cook - Restaurant/Cafeteria',
  },
  {
    value: '129',
    label: 'Delivery Person',
    industry: 'Food Service/Hotel Services',
    occupation: 'Delivery Person',
  },
  {
    value: '130',
    label: 'Desk Clerk',
    industry: 'Food Service/Hotel Services',
    occupation: 'Desk Clerk',
  },
  {
    value: '131',
    label: 'Dishwasher',
    industry: 'Food Service/Hotel Services',
    occupation: 'Dishwasher',
  },
  {
    value: '132',
    label: 'Food Production/Packing',
    industry: 'Food Service/Hotel Services',
    occupation: 'Food Production/Packing',
  },
  {
    value: '133',
    label: "Host/Maitre d'",
    industry: 'Food Service/Hotel Services',
    occupation: "Host/Maitre d'",
  },
  {
    value: '134',
    label: 'Housekeeper/Maid',
    industry: 'Food Service/Hotel Services',
    occupation: 'Housekeeper/Maid',
  },
  {
    value: '135',
    label: 'Valet',
    industry: 'Food Service/Hotel Services',
    occupation: 'Valet',
  },
  {
    value: '136',
    label: 'Waiter/Waitress',
    industry: 'Food Service/Hotel Services',
    occupation: 'Waiter/Waitress',
  },
  {
    value: '137',
    label: 'Wine Steward',
    industry: 'Food Service/Hotel Services',
    occupation: 'Wine Steward',
  },
  {
    value: '138',
    label: 'Comissioner',
    industry: 'Government/Military',
    occupation: 'Comissioner',
  },
  {
    value: '139',
    label: 'Commissioned Officer',
    industry: 'Government/Military',
    occupation: 'Officer-Commissioned',
  },
  {
    value: '140',
    label: 'Council Member',
    industry: 'Government/Military',
    occupation: 'Council Member',
  },
  {
    value: '141',
    label: 'Legislator',
    industry: 'Government/Military',
    occupation: 'Legislator',
  },
  {
    value: '142',
    label: 'Mayor/City Manager',
    industry: 'Government/Military',
    occupation: 'Mayor/City Manager',
  },
  {
    value: '143',
    label: 'Meter Reader',
    industry: 'Government/Military',
    occupation: 'Meter Reader',
  },
  {
    value: '144',
    label: 'Military',
    industry: 'Government/Military',
    occupation: 'Enlisted Military Personnel (E1-4)',
  },
  {
    value: '145',
    label: 'Park Ranger',
    industry: 'Government/Military',
    occupation: 'Park Ranger',
  },
  {
    value: '146',
    label: 'Planner',
    industry: 'Government/Military',
    occupation: 'Planner',
  },
  {
    value: '147',
    label: 'Postmaster',
    industry: 'Government/Military',
    occupation: 'Postmaster',
  },
  {
    value: '148',
    label: 'Regulator',
    industry: 'Government/Military',
    occupation: 'Regulator',
  },
  {
    value: '149',
    label: 'US Postal Worker',
    industry: 'Government/Military',
    occupation: 'US Postal Worker',
  },
  {
    value: '150',
    label: 'Warrant Officer',
    industry: 'Government/Military',
    occupation: 'Warrant Officer',
  },
  {
    value: '151',
    label: 'Homemaker',
    industry: 'Homemaker/House person',
    occupation: 'Homemaker/House person',
  },
  {
    value: '152',
    label: 'Hardware Engineer',
    industry: 'Information Technology',
    occupation: 'Engineer-Hardware',
  },
  {
    value: '153',
    label: 'Network Administrator',
    industry: 'Information Technology',
    occupation: 'Network Administrator',
  },
  {
    value: '154',
    label: 'Programmer',
    industry: 'Information Technology',
    occupation: 'Programmer',
  },
  {
    value: '155',
    label: 'Project Coordinator',
    industry: 'Information Technology',
    occupation: 'Project Coordinator',
  },
  {
    value: '156',
    label: 'Software Engineer',
    industry: 'Information Technology',
    occupation: 'Engineer-Software',
  },
  {
    value: '157',
    label: 'Support Technician',
    industry: 'Information Technology',
    occupation: 'Support Technician',
  },
  {
    value: '158',
    label: 'Systems Engineer',
    industry: 'Information Technology',
    occupation: 'Engineer-Systems',
  },
  {
    value: '159',
    label: 'Systems Security',
    industry: 'Information Technology',
    occupation: 'Systems Security',
  },
  {
    value: '160',
    label: 'Technical Writer',
    industry: 'Information Technology',
    occupation: 'Technical Writer',
  },
  {
    value: '161',
    label: 'Web Developer',
    industry: 'Information Technology',
    occupation: 'Web Developer',
  },
  {
    value: '162',
    label: 'Claims Adjuster',
    industry: 'Insurance',
    occupation: 'Claims Adjuster',
  },
  {
    value: '163',
    label: 'Customer Service Representative',
    industry: 'Insurance',
    occupation: 'Customer Service Representative',
  },
  {
    value: '164',
    label: 'Director/Administrator',
    industry: 'Insurance',
    occupation: 'Director/Administrator',
  },
  {
    value: '165',
    label: 'Executive',
    industry: 'Insurance',
    occupation: 'Executive',
  },
  {
    value: '166',
    label: 'Insurance Agent',
    industry: 'Insurance',
    occupation: 'Insurance Agent',
  },
  {
    value: '167',
    label: 'Underwriter',
    industry: 'Insurance',
    occupation: 'Underwriter',
  },
  {
    value: '168',
    label: 'Airport Security Officer',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Airport Security Officer',
  },
  {
    value: '169',
    label: 'Animal Control Officer',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Animal Control Officer',
  },
  {
    value: '170',
    label: 'Attorney',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Attorney',
  },
  {
    value: '171',
    label: 'Bailiff',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Bailiff',
  },
  {
    value: '172',
    label: 'Corrections Officer',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Corrections Officer',
  },
  {
    value: '173',
    label: 'Court Clerk/Reporter',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Court Clerk/Reporter',
  },
  {
    value: '174',
    label: 'Deputy Sheriff',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Deputy Sheriff',
  },
  {
    value: '175',
    label: 'Dispatcher',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Dispatcher',
  },
  {
    value: '176',
    label: 'Examiner',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Examiner',
  },
  {
    value: '177',
    label: 'Federal Agent',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Federal Agent/Marshall',
  },
  {
    value: '178',
    label: 'Fire Chief',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Fire Chief',
  },
  {
    value: '179',
    label: 'Fire Fighter',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Fire Fighter/Supervisor',
  },
  {
    value: '180',
    label: 'Gaming Officer',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Gaming Officer/Investigator',
  },
  {
    value: '181',
    label: 'Highway Patrol Officer',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Highway Patrol Officer',
  },
  {
    value: '182',
    label: 'Judge/Hearing Officer',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Judge/Hearing Officer',
  },
  {
    value: '183',
    label: 'Legal Assistant/Secretary',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Legal Assistant/Secretary',
  },
  {
    value: '184',
    label: 'Paralegal/Lawk Clerk',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Paralegal/Law Clerk',
  },
  {
    value: '185',
    label: 'Police Chief',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Police Chief',
  },
  {
    value: '186',
    label: 'Police Detective',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Police Detective',
  },
  {
    value: '187',
    label: 'Police Officer',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Police Officer/Supervisor',
  },
  {
    value: '188',
    label: 'Private Investigator',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Private Investigator',
  },
  {
    value: '189',
    label: 'Process Server',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Process Server',
  },
  {
    value: '190',
    label: 'Security Guard',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Security Guard',
  },
  {
    value: '191',
    label: 'Sheriff',
    industry: 'Legal/Law Enforcement/Security',
    occupation: 'Sheriff',
  },
  {
    value: '192',
    label: 'Certified Nursing Assistant',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Nurse - C.N.A.',
  },
  {
    value: '193',
    label: 'Chiropractor',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Chiropractor',
  },
  {
    value: '194',
    label: 'Clergy',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Clergy',
  },
  {
    value: '195',
    label: 'Client Care Worker',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Client Care Worker',
  },
  {
    value: '196',
    label: 'Dental Hygenist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Dental Hygenist',
  },
  {
    value: '197',
    label: 'Dentist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Dentist',
  },
  {
    value: '198',
    label: 'Dietician',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Dietician',
  },
  {
    value: '199',
    label: 'Doctor',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Doctor',
  },
  {
    value: '200',
    label: 'Hospice Volunteer',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Hospice Volunteer',
  },
  {
    value: '201',
    label: 'Licensed Practical Nurse',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Nurse - LPN',
  },
  {
    value: '202',
    label: 'Medical Assistant',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Other',
  },
  {
    value: '203',
    label: 'Mortician',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Mortician',
  },
  {
    value: '204',
    label: 'Nurse Practitioner',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Nurse Practitioner',
  },
  {
    value: '205',
    label: 'Optometrist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Optometrist',
  },
  {
    value: '206',
    label: 'Orthodontist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Orthodontist',
  },
  {
    value: '207',
    label: 'Paramedic',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Paramedic / EM Technician',
  },
  {
    value: '208',
    label: 'Pharmacist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Pharmacist',
  },
  {
    value: '209',
    label: 'Physical Therapist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Physical Therapist',
  },
  {
    value: '210',
    label: 'Psychologist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Psychologist',
  },
  {
    value: '211',
    label: 'Registered Nurse',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Nurse - RN',
  },
  {
    value: '212',
    label: 'Social Worker',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Social Worker',
  },
  {
    value: '213',
    label: 'Support Services',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Support Services',
  },
  {
    value: '214',
    label: 'Therapist',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Therapist',
  },
  {
    value: '215',
    label: 'Veterinarian',
    industry: 'Medical/Social Services/Religion',
    occupation: 'Veterinarian',
  },
  {
    value: '216',
    label: 'Caregiver',
    industry: 'Personal Care/Service',
    occupation: 'Caregiver',
  },
  {
    value: '217',
    label: 'Dry Cleaner/Laundry',
    industry: 'Personal Care/Service',
    occupation: 'Dry Cleaner/Laundry',
  },
  {
    value: '218',
    label: 'Hair Stylist/Barber',
    industry: 'Personal Care/Service',
    occupation: 'Hair Stylist/Barber',
  },
  {
    value: '219',
    label: 'Manicurist',
    industry: 'Personal Care/Service',
    occupation: 'Manicurist',
  },
  {
    value: '220',
    label: 'Masseuse',
    industry: 'Personal Care/Service',
    occupation: 'Masseuse',
  },
  {
    value: '221',
    label: 'Nanny',
    industry: 'Personal Care/Service',
    occupation: 'Nanny',
  },
  {
    value: '222',
    label: 'Pet Services',
    industry: 'Personal Care/Service',
    occupation: 'Pet Services',
  },
  {
    value: '223',
    label: 'Factory Worker',
    industry: 'Production/Manufacturing',
    occupation: 'Factory Worker',
  },
  {
    value: '224',
    label: 'Furniture Finisher',
    industry: 'Production/Manufacturing',
    occupation: 'Furniture Finisher',
  },
  {
    value: '225',
    label: 'Inspector',
    industry: 'Production/Manufacturing',
    occupation: 'Inspector',
  },
  {
    value: '226',
    label: 'Jeweler',
    industry: 'Production/Manufacturing',
    occupation: 'Jeweler',
  },
  {
    value: '227',
    label: 'Machine Operator',
    industry: 'Production/Manufacturing',
    occupation: 'Machine Operator',
  },
  {
    value: '228',
    label: 'Packer',
    industry: 'Production/Manufacturing',
    occupation: 'Packer',
  },
  {
    value: '229',
    label: 'Plant Manager',
    industry: 'Production/Manufacturing',
    occupation: 'Plant Manager',
  },
  {
    value: '230',
    label: 'Bookbinder',
    industry: 'Production/Manufacturing',
    occupation: 'Printer/Bookbinder',
  },
  {
    value: '231',
    label: 'Quality Control',
    industry: 'Production/Manufacturing',
    occupation: 'Quality Control',
  },
  {
    value: '232',
    label: 'Refining Operator',
    industry: 'Production/Manufacturing',
    occupation: 'Refining Operator',
  },
  {
    value: '233',
    label: 'Shoemaker',
    industry: 'Production/Manufacturing',
    occupation: 'Shoemaker',
  },
  {
    value: '234',
    label: 'Tailor/Custom Sewer',
    industry: 'Production/Manufacturing',
    occupation: 'Tailor/Custom Sewer',
  },
  {
    value: '235',
    label: 'Textile Worker',
    industry: 'Production/Manufacturing',
    occupation: 'Textile Worker',
  },
  {
    value: '236',
    label: 'Upholsterer',
    industry: 'Production/Manufacturing',
    occupation: 'Upholsterer',
  },
  {
    value: '237',
    label: 'Building Maintanence Engineer',
    industry: 'Repair/Maintenance/Grounds',
    occupation: 'Building Maintanence Engineer',
  },
  {
    value: '238',
    label: 'Custodian/Janitor',
    industry: 'Repair/Maintenance/Grounds',
    occupation: 'Custodian/Janitor',
  },
  {
    value: '239',
    label: 'Field Service Technician',
    industry: 'Repair/Maintenance/Grounds',
    occupation: 'Field Service Technician',
  },
  {
    value: '240',
    label: 'Handyman',
    industry: 'Repair/Maintenance/Grounds',
    occupation: 'Handyman',
  },
  {
    value: '241',
    label: 'Heat/AC Repairman',
    industry: 'Repair/Maintenance/Grounds',
    occupation: 'Heat/AC Repairman',
  },
  {
    value: '242',
    label: 'Landscape/Grounds Maintanence',
    industry: 'Repair/Maintenance/Grounds',
    occupation: 'Landscape/Grounds Maintanence',
  },
  {
    value: '243',
    label: 'Mechanic',
    industry: 'Repair/Maintenance/Grounds',
    occupation: 'Mechanic',
  },
  {
    value: '244',
    label: 'Retired',
    industry: 'Retired',
    occupation: 'Retired',
  },
  {
    value: '245',
    label: 'Activity/Recreational Assistant',
    industry: 'Sports/Recreation',
    occupation: 'Activity/Recreational Assistant',
  },
  {
    value: '246',
    label: 'Agent',
    industry: 'Sports/Recreation',
    occupation: 'Agent',
  },
  {
    value: '247',
    label: 'Athlete',
    industry: 'Sports/Recreation',
    occupation: 'Athlete',
  },
  {
    value: '248',
    label: 'Camp Counselor/Lead',
    industry: 'Sports/Recreation',
    occupation: 'Camp Counselor/Lead',
  },
  {
    value: '249',
    label: 'Coach',
    industry: 'Sports/Recreation',
    occupation: 'Coach',
  },
  {
    value: '250',
    label: 'Event Manager/Promoter',
    industry: 'Sports/Recreation',
    occupation: 'Event Manager/Promoter',
  },
  {
    value: '251',
    label: 'Life Guard',
    industry: 'Sports/Recreation',
    occupation: 'Life Guard',
  },
  {
    value: '252',
    label: 'Sports Broadcaster/Journalist',
    industry: 'Sports/Recreation',
    occupation: 'Sports Broadcaster/Journalist',
  },
  {
    value: '253',
    label: 'Trainer/Instructor',
    industry: 'Sports/Recreation',
    occupation: 'Trainer/Instructor',
  },
  {
    value: '254',
    label: 'Umpire/Referee',
    industry: 'Sports/Recreation',
    occupation: 'Umpire/Referee',
  },
  {
    value: '255',
    label: 'Student',
    industry: 'Student',
    occupation: 'Graduate Student',
  },
  {
    value: '256',
    label: 'Air Traffic Control',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Air Traffic Control',
  },
  {
    value: '257',
    label: 'Airport Operations Crew',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Airport Operations Crew',
  },
  {
    value: '258',
    label: 'Bus/Streetcar Driver',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Driver - Bus/Streetcar',
  },
  {
    value: '259',
    label: 'Crane Loader/Operator',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Crane Loader/Operator',
  },
  {
    value: '260',
    label: 'Flight Attendant',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Flight Attendant',
  },
  {
    value: '261',
    label: 'Forklift Operator',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Forklift Operator',
  },
  {
    value: '262',
    label: 'Longshoreman',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Longshoreman',
  },
  {
    value: '263',
    label: 'Mate/Sailor',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Mate/Sailor',
  },
  {
    value: '264',
    label: 'Parking Lot Attendant',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Parking Lot Attendant',
  },
  {
    value: '265',
    label: 'Pilot/Captain',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Pilot/Captain/Engineer',
  },
  {
    value: '266',
    label: 'Porter',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Bellhop/Porter',
  },
  {
    value: '267',
    label: 'Railroad Worker',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Railroad Worker',
  },
  {
    value: '268',
    label: 'Shipping/Receiving Clerk',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Shipping/Receiving Clerk',
  },
  {
    value: '269',
    label: 'Subway/Light Rail Operator',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Subway/Light Rail Operator',
  },
  {
    value: '270',
    label: 'Taxi/Limo Driver',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Driver-Taxi/Limo',
  },
  {
    value: '271',
    label: 'Ticket Agent',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Ticket Agent',
  },
  {
    value: '272',
    label: 'Transportation Specialist',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Transportation Specialist',
  },
  {
    value: '273',
    label: 'Travel Agent',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Travel Agent',
  },
  {
    value: '274',
    label: 'Truck/Delivery Driver',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Driver-Truck/Delivery',
  },
  {
    value: '275',
    label: 'Warehouse Manager',
    industry: 'Travel/Transportation/Storage',
    occupation: 'Manager - Warehouse/District',
  },
  {
    value: '276',
    label: 'Unemployed',
    industry: 'Unemployed',
    occupation: 'Unemployed',
  },
];

export const getOccupationList = (): OCCUPATION[] => {
  return OCCUPATIONS;
};

/**
 * New popular occupations list
 * https://metabase.ing.getjerry.com/question/15262-most-popular-occupations?start_date=2024-06-01
 */
const POPULAR_OCCUPATIONS_LIST2: Array<{
  industry: string;
  occupation: string;
}> = [
  {
    industry: 'Construction/Energy Trades',
    occupation: 'Laborer/Worker',
  },
  {
    industry: 'Business/Sales/Office',
    occupation: 'Manager - General Operations',
  },
  {
    occupation: 'Sales - Retail/Wholesale',
    industry: 'Business/Sales/Office',
  },
  {
    occupation: 'Customer Service Representative',
    industry: 'Insurance',
  },
  {
    industry: 'Travel/Transportation/Storage',
    occupation: 'Driver-Truck/Delivery',
  },
  {
    industry: 'Engineer/Architect/Science/Math',
    occupation: 'Engineer',
  },
  {
    industry: 'Education/Library',
    occupation: 'Teacher, College',
  },
  {
    industry: 'Banking/Finance/Real Estate',
    occupation: 'Analyst',
  },
  {
    industry: 'Business/Sales/Office',
    occupation: OTHER_OCCUPATION,
  },
];

export const DefaultEmployedOccupationValue = '64';

/**
 * Given a driver, find value of matching occupation/industry mapping.
 * Defaults to 'Other'
 */
export const getOccupationValue = (driver?: Driver, useOccupation = false, useDefault = true): string => {
  const options = useOccupation ? getOccupationList() : EMPLOYMENT_OPTIONS;
  const driverOccupation = find(
    options,
    (opt) => opt.industry === driver?.industry && opt.occupation === driver?.occupation
  );
  if (driverOccupation) {
    return driverOccupation.value;
  }
  // Return 'Other' options' value.
  return useDefault ? DefaultEmployedOccupationValue : '';
};

/**
 * Get occupation object by given value.
 * @param value
 */
export const getOccupationByValue = (value: string | undefined, useOccupation = false): OCCUPATION | undefined => {
  const options = useOccupation ? getOccupationList() : EMPLOYMENT_OPTIONS_WITH_DECLINE;
  return find(options, (occupation) => occupation.value === value);
};

/**
 * Get the unpopular occupations and group them by first letter of the label
 * @param occupations
 */
export const getUnpopularOccupationsGroupedByFirstLabelLetter = (
  occupations: OCCUPATION[]
): Record<string, OCCUPATION[]> => {
  const filteredOccupations = filter(occupations, (item) => {
    const isPopular = !!find(POPULAR_OCCUPATIONS_LIST2, (popularItem) => {
      return item.occupation === popularItem.occupation && item.industry === popularItem.industry;
    });

    return !isPopular && item.label !== OTHER_OCCUPATION;
  });

  return groupBy(
    sortBy(filteredOccupations, (occupation) => occupation.label),
    ({ label }) => upperCase(label[0])
  );
};

/**
 * Get popular occupations
 * @param occupations
 */
export const getPopularOccupations = (occupations: OCCUPATION[]): OCCUPATION[] => {
  const filteredOccupations: OCCUPATION[] = [];

  forEach(POPULAR_OCCUPATIONS_LIST2, (popularItem) => {
    const occupation = find(occupations, (item) => {
      return item.occupation === popularItem.occupation && item.industry === popularItem.industry;
    });

    if (occupation) {
      filteredOccupations.push(occupation);
    }
  });

  return filteredOccupations;
};

/**
 * Check if the given occupation is valid
 */
export const isValidOccupation = (occupation?: string | null): boolean => {
  const options = getOccupationList();
  return !!find(options, (opt) => opt.occupation === occupation);
};

/**
 * Check if the given industry & occupation belongs to 'Employed'
 * @param industry
 * @param occupation
 * @returns
 */
export const isEmployed = (industry?: string | null, occupation?: string | null, useOccupation = false): boolean => {
  if (useOccupation) {
    return !!find(getOccupationList(), (opt) => opt.industry === industry && opt.occupation === occupation);
  } else {
    const employedOccupation = getEmployedOccupationOption();
    return employedOccupation.industry === industry && employedOccupation.occupation === occupation;
  }
};

/**
 * Get the employed occupation option
 * @returns
 */
export const getEmployedOccupationOption = (): OCCUPATION =>
  find(EMPLOYMENT_OPTIONS, (opt) => opt.industry === 'Business/Sales/Office' && opt.occupation === 'Other')!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

/**
 * Get the default occupation list option
 * @returns
 */
export const getDefaultOccupationListOption = (): OCCUPATION =>
  find(OCCUPATIONS, (opt) => opt.industry === 'Business/Sales/Office' && opt.occupation === 'Other')!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
